import { fetchClient } from '../lib/openapi-query';
import {
  type PaginatedResponse,
  type ProductCatalog,
  type ProductCategory,
  type ProductDetails,
  type ProductListItem,
  type ProductListQuery
} from '../types/products';
import { mockProductsApi } from './products-mock';

// Use mock data in development when API fails - disabled by default
const USE_MOCK_FALLBACK = false;

const withMockFallback = async <T>(
  apiCall: () => Promise<T>,
  mockCall: () => Promise<T>
): Promise<T> => {
  if (!USE_MOCK_FALLBACK) {
    return apiCall();
  }
  
  try {
    return await apiCall();
  } catch (error) {
    console.warn('API call failed, using mock data:', error);
    return mockCall();
  }
};

export const productsApi = {
  getCategories: async (): Promise<ProductCategory[]> => {
    return withMockFallback(
      async () => {
        const response = await fetchClient.GET('/products/categories');
        if (response.error) {
          throw new Error('Failed to fetch categories');
        }
        return response.data as unknown as ProductCategory[];
      },
      () => mockProductsApi.getCategories()
    );
  },

  getHealthGoals: async (): Promise<ProductCategory[]> => {
    return withMockFallback(
      async () => {
        const response = await fetchClient.GET('/products/health-goals');
        if (response.error) {
          throw new Error('Failed to fetch health goals');
        }
        return response.data as unknown as ProductCategory[];
      },
      () => mockProductsApi.getHealthGoals()
    );
  },

  getProducts: async (params: Partial<ProductListQuery>): Promise<PaginatedResponse<ProductListItem>> => {
    return withMockFallback(
      async () => {
        const queryParams = new URLSearchParams();

        if (params.id) {queryParams.append('id', params.id);}
        if (params.search) {queryParams.append('search', params.search);}
        if (params.categories) {
          const categoriesStr = Array.isArray(params.categories)
            ? params.categories.join(',')
            : params.categories;
          queryParams.append('categories', categoriesStr);
        }
        if (params.healthGoals) {
          const healthGoalsStr = Array.isArray(params.healthGoals)
            ? params.healthGoals.join(',')
            : params.healthGoals;
          queryParams.append('healthGoals', healthGoalsStr);
        }
        if (params.sortBy) {queryParams.append('sortBy', params.sortBy);}
        if (params.sortOrder) {queryParams.append('sortOrder', params.sortOrder);}
        if (params.page) {queryParams.append('page', params.page.toString());}
        if (params.perPage) {queryParams.append('perPage', params.perPage.toString());}
        if (params.locale) {queryParams.append('locale', params.locale);}

        const response = await fetchClient.GET('/products', {
          params: {
            query: Object.fromEntries(queryParams.entries())
          }
        });

        if (response.error) {
          throw new Error('Failed to fetch products');
        }

        return response.data as unknown as PaginatedResponse<ProductListItem>;
      },
      () => mockProductsApi.getProducts(params)
    );
  },

  getProductsByCategory: async (
    categoryId: string,
    params?: { page?: number; perPage?: number }
  ): Promise<PaginatedResponse<ProductListItem>> => {
    return withMockFallback(
      async () => {
        const queryParams = new URLSearchParams();
        if (params?.page) {queryParams.append('page', params.page.toString());}
        if (params?.perPage) {queryParams.append('perPage', params.perPage.toString());}

        const response = await fetchClient.GET('/products/categories/{categoryId}/products', {
          params: {
            path: { categoryId },
            query: Object.fromEntries(queryParams.entries())
          }
        });
        if (response.error) {
          throw new Error('Failed to fetch products by category');
        }
        return response.data as unknown as PaginatedResponse<ProductListItem>;
      },
      () => mockProductsApi.getProductsByCategory(categoryId, params)
    );
  },

  getProductHome: async (params?: {
    productsPerCategory?: string;
    topProductsContext?: 'onboarding' | 'product-plan' | 'user-product-plan';
    productPlanName?: string;
  }): Promise<ProductCatalog> => {
    return withMockFallback(
      async () => {
        const queryParams = new URLSearchParams();
        if (params?.productsPerCategory) {
          queryParams.append('productsPerCategory', params.productsPerCategory);
        }
        if (params?.topProductsContext) {
          queryParams.append('topProductsContext', params.topProductsContext);
        }
        if (params?.productPlanName) {
          queryParams.append('productPlanName', params.productPlanName);
        }

        const response = await fetchClient.GET('/products/home', {
          params: {
            query: Object.fromEntries(queryParams.entries())
          }
        });
        if (response.error) {
          throw new Error('Failed to fetch product catalog');
        }
        return response.data as unknown as ProductCatalog;
      },
      () => mockProductsApi.getProductHome(params)
    );
  },

  getProductDetails: async (productId: string): Promise<ProductDetails> => {
    return withMockFallback(
      async () => {
        const response = await fetchClient.GET('/products/{productId}', {
          params: {
            path: { productId }
          }
        });
        
        if (response.error) {
          throw new Error('Failed to fetch product details');
        }

        return response.data as unknown as ProductDetails;
      },
      () => mockProductsApi.getProductDetails(productId)
    );
  }
};